import {FC, useMemo} from 'react'
import {HttpGetSignatureSheetsResponse, HttpValidateAccessesOptions} from '../../http/accesses.http'
import {
    StyledAccessesValidatorListItemContainer,
    StyledAccessesValidatorListItemCounterWrapper,
    StyledAccessesValidatorListItemStatusSquare,
    StyledAccordionContentInner,
    StyledOpenAccordionButton,
    StyledAccordionWrapper,
    StyledPatientCell,
    StyledTruncatedText
} from './style'
import {Text} from '@/components/ui/text/Text'
import {AlertCircleIcon, ChevronDownIcon} from '@/components/ui/icon/Icon'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {formatLocaleDate} from '@/dayjs'
import {capitalize} from '@/utilities/helpers'
import {useTranslation} from '@/translations/i18n'
import {accessesStatusToLabel} from '../../utils'
import {AccessesValidatorOtherActionMenu} from '../accesses-validator-other-action-menu/AccessesValidatorOtherActionMenu'
import {Divider} from '@/components/ui/divider/Divider'
import {AccessesValidatorListItemAccess} from './AccessesValidatorListItemAccess'
import {useTheme} from 'styled-components'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {MUTATION_KEYS} from '@/queryClient'
import {useMutationState} from '@tanstack/react-query'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useAccessesAccordion} from '../accesses-accordion-context-provider/AccessesAccordionContextProvider'

export const AccessesValidatorListItem: FC<{signatureSheet: HttpGetSignatureSheetsResponse[0]}> = ({
    signatureSheet
}) => {
    const {t} = useTranslation()
    const {expandedItems, expandItem, collapseItem} = useAccessesAccordion()
    const itemID = useMemo(() => `${signatureSheet.priCode}-${signatureSheet.period}`, [signatureSheet])
    const handleToggle = () => {
        if (expandedItems.includes(itemID)) {
            collapseItem(itemID)
        } else {
            expandItem(itemID)
        }
    }

    const absences = signatureSheet.accesses.filter(access => access.type === 'absence')

    const {palette} = useTheme()

    const validatingAccessesMutation = useMutationState({
        filters: {
            mutationKey: [MUTATION_KEYS.VALIDATE_ACCESS],
            status: 'pending'
        },
        select: mutation => mutation.state.variables as HttpValidateAccessesOptions['payload']
    })

    return (
        <StyledAccordionWrapper>
            <StyledAccessesValidatorListItemContainer>
                <StyledOpenAccordionButton
                    $isExpanded={expandedItems.includes(itemID)}
                    variant="ghost"
                    onClick={handleToggle}
                >
                    <ChevronDownIcon />
                </StyledOpenAccordionButton>
                <Text size="textSm" palette="neutral" shade="600">
                    {capitalize(formatLocaleDate(signatureSheet.period, 'MMMM YYYY'))}
                </Text>
                <StyledPatientCell direction="column" align="stretch">
                    <Text size="textSm" palette="neutral" shade="600">
                        {t('commons:patient')}
                    </Text>
                    <Tooltip variant="light" content={signatureSheet.patientFullName} triggerProps={{asChild: true}}>
                        <StyledTruncatedText size="textSm" palette="neutral" shade="950" weight="500">
                            {signatureSheet.patientFullName}
                        </StyledTruncatedText>
                    </Tooltip>
                </StyledPatientCell>
                <Flexbox direction="column">
                    <Text size="textSm" palette="neutral" shade="600">
                        {t('commons:pri_n')}
                    </Text>
                    <Text size="textSm" palette="neutral" shade="950" weight="500">
                        {signatureSheet.priCode}
                    </Text>
                </Flexbox>

                <StyledAccessesValidatorListItemCounterWrapper>
                    <Text size="textSm" palette="neutral" shade="600">
                        {t('commons:treatments')}
                    </Text>
                    <Text size="textLg" palette="neutral" shade="600" weight="700">
                        {signatureSheet.numberOfTreatments}
                    </Text>
                </StyledAccessesValidatorListItemCounterWrapper>
                <StyledAccessesValidatorListItemCounterWrapper>
                    <Text size="textSm" palette="neutral" shade="600">
                        {t('agenda:secondary_treatment')}
                    </Text>
                    <Text size="textLg" palette="neutral" shade="600" weight="700">
                        {signatureSheet.numberOfOtherTreatments}
                    </Text>
                </StyledAccessesValidatorListItemCounterWrapper>
                <StyledAccessesValidatorListItemCounterWrapper>
                    <Flexbox gap={1}>
                        <Text size="textSm" palette="neutral" shade="600">
                            {t('agenda:patient_absence')}
                        </Text>
                        {absences.some(absence => absence.event.communicatedOnTime === null) && (
                            <Tooltip variant="light" content={t('accesses:absence_not_communicated')}>
                                <AlertCircleIcon color={palette.orange['500']} size={15} />
                            </Tooltip>
                        )}
                    </Flexbox>
                    <Text size="textLg" palette="neutral" shade="600" weight="700">
                        {signatureSheet.numberOfAbsences}
                    </Text>
                </StyledAccessesValidatorListItemCounterWrapper>

                {validatingAccessesMutation.some(mutation =>
                    signatureSheet.accesses.some(access => mutation.accessIds.includes(access.id))
                ) ? (
                    <Skeleton height={20} width={60} />
                ) : (
                    <Flexbox align="center" gap={1} style={{marginLeft: 12}}>
                        <StyledAccessesValidatorListItemStatusSquare $status={signatureSheet.globalStatus} />
                        <Text size="textSm" palette="neutral" shade="600">
                            {t(accessesStatusToLabel[signatureSheet.globalStatus])}
                        </Text>
                    </Flexbox>
                )}

                <Flexbox fullWidth justify="end">
                    <AccessesValidatorOtherActionMenu signatureSheet={signatureSheet} />
                </Flexbox>
            </StyledAccessesValidatorListItemContainer>
            {expandedItems.includes(itemID) && (
                <div>
                    <Divider direction="horizontal" />

                    <StyledAccordionContentInner>
                        {signatureSheet.accesses.map(access => (
                            <AccessesValidatorListItemAccess key={access.id} access={access} />
                        ))}
                    </StyledAccordionContentInner>
                </div>
            )}
        </StyledAccordionWrapper>
    )
}
